import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Clapperboard, Film, Save, Settings2, RotateCcw } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { DEFAULT_SCREEN_CONFIGS, DEFAULT_ANIMATION_SYSTEM_PROMPTS } from '../../constants/slot-screen-defaults';
import type { SlotScreenId } from '@core/types/slot-screen-generator.types';

interface PromptEditorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  screenId: SlotScreenId;
  field: 'description' | 'animation' | 'animation-system';
}

export function PromptEditorDrawer({ isOpen, onClose, screenId, field }: PromptEditorDrawerProps) {
  const [localText, setLocalText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const storeText = useSlotScreenGeneratorStore(s =>
    field === 'description'
      ? s.screens[screenId]?.userPrompt ?? ''
      : field === 'animation'
        ? s.videoSlotImages[screenId]?.animationPrompt ?? ''
        : s.videoSlotImages[screenId]?.animationSystemPrompt ?? ''
  );
  const updateScreenPrompt = useSlotScreenGeneratorStore(s => s.updateScreenPrompt);
  const setVideoSlotAnimationPrompt = useSlotScreenGeneratorStore(s => s.setVideoSlotAnimationPrompt);
  const setVideoSlotAnimationSystemPrompt = useSlotScreenGeneratorStore(s => s.setVideoSlotAnimationSystemPrompt);
  const resetVideoSlotAnimationSystemPrompt = useSlotScreenGeneratorStore(s => s.resetVideoSlotAnimationSystemPrompt);

  const isDirty = localText !== savedText;
  const config = DEFAULT_SCREEN_CONFIGS[screenId];
  const title = field === 'description' ? 'Description Prompt'
    : field === 'animation' ? 'Animation Prompt'
    : 'Animation System Prompt';
  const Icon = field === 'description' ? Clapperboard : field === 'animation' ? Film : Settings2;
  const placeholder = field === 'description'
    ? 'Describe this screen in detail...'
    : field === 'animation'
      ? 'Describe the animation for the video... e.g. \'zoom in with golden sparkles\''
      : 'Global + screen-specific animation rules for video generation...';

  // Initialize local text from store when drawer opens
  useEffect(() => {
    if (isOpen) {
      setLocalText(storeText);
      setSavedText(storeText);
      setShowCloseWarning(false);
      // Focus textarea after mount
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [isOpen, screenId, field]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(() => {
    if (field === 'description') {
      updateScreenPrompt(screenId, localText);
    } else if (field === 'animation') {
      setVideoSlotAnimationPrompt(screenId, localText);
    } else {
      setVideoSlotAnimationSystemPrompt(screenId, localText);
    }
    setSavedText(localText);
  }, [field, screenId, localText, updateScreenPrompt, setVideoSlotAnimationPrompt, setVideoSlotAnimationSystemPrompt]);

  // Ctrl+S to save
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, save]);

  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setShowCloseWarning(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleSaveAndClose = useCallback(() => {
    save();
    setShowCloseWarning(false);
    onClose();
  }, [save, onClose]);

  const handleDiscard = useCallback(() => {
    setShowCloseWarning(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-[61] bg-zinc-900 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-zinc-400" />
          <span className="text-sm font-medium text-zinc-200">
            {config?.name} — {title}
            {isDirty && <span className="text-yellow-400 ml-1">●</span>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {field === 'animation-system' && (
            <button
              onClick={() => {
                resetVideoSlotAnimationSystemPrompt(screenId);
                const def = DEFAULT_ANIMATION_SYSTEM_PROMPTS[screenId];
                setLocalText(def);
                setSavedText(def);
              }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200 border border-zinc-700 rounded transition-colors"
            >
              <RotateCcw size={11} />
              Reset Default
            </button>
          )}
          <button
            onClick={save}
            disabled={!isDirty}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
              isDirty
                ? 'bg-blue-500 hover:bg-blue-400 text-white shadow-md shadow-blue-500/25'
                : 'bg-zinc-800 text-zinc-600 border border-zinc-700 cursor-not-allowed'
            }`}
          >
            <Save size={12} />
            Save
          </button>
          <button
            onClick={handleRequestClose}
            className="p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden p-4">
        <textarea
          ref={textareaRef}
          value={localText}
          onChange={e => setLocalText(e.target.value)}
          placeholder={placeholder}
          className="w-full h-full bg-zinc-950 border border-zinc-700 rounded-lg px-4 py-3 text-sm leading-relaxed text-zinc-200 resize-none outline-none focus:ring-1 focus:ring-blue-500/50 placeholder:text-zinc-600"
        />
      </div>

      {/* VS Code-style save dialog */}
      {showCloseWarning && (
        <>
          <div className="absolute inset-0 z-[70] bg-black/50" />
          <div className="absolute inset-0 z-[71] flex items-center justify-center">
            <div className="bg-zinc-800 border border-zinc-600 rounded-lg shadow-2xl w-[340px] overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h3 className="text-sm font-medium text-zinc-200">
                  Do you want to save changes?
                </h3>
                <p className="text-xs text-zinc-400 mt-1.5">
                  Your changes will be lost if you don't save them.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 pb-4">
                <button
                  onClick={handleSaveAndClose}
                  className="px-4 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleDiscard}
                  className="px-4 py-1.5 text-xs font-medium border border-zinc-600 text-zinc-300 hover:bg-zinc-700 rounded transition-colors"
                >
                  Don't Save
                </button>
                <button
                  onClick={() => setShowCloseWarning(false)}
                  className="px-4 py-1.5 text-xs font-medium border border-zinc-600 text-zinc-300 hover:bg-zinc-700 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
